import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, CheckCircle, XCircle, Minus } from 'lucide-react';

const MONTH_NAMES = ['Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const DAY_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

export default function CalendarPage() {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredDay, setHoveredDay] = useState(null);

  const monthStr = `${currentMonth.year}-${String(currentMonth.month + 1).padStart(2, '0')}`;

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/calendar?month=${monthStr}`);
      setCalendarData(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [monthStr]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const prevMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 0) return { year: prev.year - 1, month: 11 };
      return { ...prev, month: prev.month - 1 };
    });
  };

  const nextMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 11) return { year: prev.year + 1, month: 0 };
      return { ...prev, month: prev.month + 1 };
    });
  };

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const daysInMonth = getDaysInMonth(currentMonth.year, currentMonth.month);
  const firstDay = getFirstDayOfMonth(currentMonth.year, currentMonth.month);

  const getStatusForDay = (day) => {
    const dateStr = `${currentMonth.year}-${String(currentMonth.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendarData.find(d => d.date === dateStr);
  };

  const today = new Date();
  const isToday = (day) => {
    return today.getFullYear() === currentMonth.year && today.getMonth() === currentMonth.month && today.getDate() === day;
  };

  const successDays = calendarData.filter(d => d.status === 'success').length;
  const relapseDays = calendarData.filter(d => d.status === 'relapse').length;

  const cells = [];
  for (let i = 0; i < firstDay; i++) {
    cells.push(<div key={`empty-${i}`} />);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const status = getStatusForDay(day);
    const todayClass = isToday(day) ? 'ring-2 ring-primary ring-offset-2' : '';
    let bgClass = 'bg-muted/30 hover:bg-muted/60';
    let statusIcon = null;
    if (status?.status === 'success') {
      bgClass = 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200';
      statusIcon = <CheckCircle className="w-3 h-3 text-green-600 dark:text-green-400" />;
    } else if (status?.status === 'relapse') {
      bgClass = 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200';
      statusIcon = <XCircle className="w-3 h-3 text-red-500" />;
    }

    cells.push(
      <div
        key={day}
        className={`relative flex flex-col items-center justify-center p-2 rounded-xl cursor-default transition-all ${bgClass} ${todayClass}`}
        onMouseEnter={() => setHoveredDay(status ? { day, ...status } : null)}
        onMouseLeave={() => setHoveredDay(null)}
        data-testid={`calendar-day-${day}`}
      >
        <span className="text-sm font-medium">{day}</span>
        {statusIcon && <div className="mt-0.5">{statusIcon}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div>
        <h1 className="text-3xl font-heading font-bold tracking-tight">Calendario</h1>
        <p className="text-muted-foreground text-sm mt-1">Visualize seu progresso ao longo do tempo</p>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-green-100 dark:bg-green-900/40 border border-green-300 dark:border-green-700" />
          <span className="text-xs text-muted-foreground">Sucesso ({successDays})</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-700" />
          <span className="text-xs text-muted-foreground">Recaida ({relapseDays})</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-muted border border-border" />
          <span className="text-xs text-muted-foreground">Sem registro</span>
        </div>
      </div>

      <Card data-testid="calendar-card">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={prevMonth} data-testid="calendar-prev-month">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h2 className="text-xl font-heading font-semibold">
              {MONTH_NAMES[currentMonth.month]} {currentMonth.year}
            </h2>
            <Button variant="ghost" size="icon" onClick={nextMonth} data-testid="calendar-next-month">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {DAY_NAMES.map(d => (
              <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1">{d}</div>
            ))}
          </div>
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-2">
            {loading ? (
              Array.from({ length: 35 }).map((_, i) => (
                <div key={i} className="h-12 bg-muted/30 rounded-xl animate-pulse" />
              ))
            ) : cells}
          </div>
        </CardContent>
      </Card>

      {/* Hover tooltip */}
      {hoveredDay && (
        <Card className="animate-fade-up" data-testid="calendar-day-details">
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-2">
              {hoveredDay.status === 'success' ? (
                <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Sucesso</Badge>
              ) : (
                <Badge variant="destructive">Recaida</Badge>
              )}
              <span className="text-sm">Dia {hoveredDay.day}</span>
            </div>
            {hoveredDay.notes && <p className="text-sm text-muted-foreground mt-1">{hoveredDay.notes}</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
